import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["items", "template", "bulkInput", "singleInput", "bulkAddContainer", "limitWarning", "progress", "progressBar", "progressText"];
  static values = {
    maxItems: { type: Number, default: 50 },
    maxBulkItems: { type: Number, default: 20 },
    currentCount: { type: Number, default: 0 },
    taskId: String
  };

  connect() {
    this.updateCount();
  }

  async saveCompletion(event) {
    const checkbox = event.currentTarget;
    const wrapper = checkbox.closest(".checklist-item");
    const status = wrapper?.querySelector("[data-checklist-save-status]");
    const url = checkbox.dataset.checklistCompletionUrl;

    if (!url || wrapper?.dataset.newRecord === "true") return;

    checkbox.disabled = true;
    this.showSaveStatus(status, "Saving…", "text-slate-400");

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ completed: checkbox.checked })
      });
      const payload = await response.json();

      if (!response.ok) throw new Error(payload.error || "Could not save this subtask");

      checkbox.checked = payload.actor_completed;
      this.updateConfirmationState(wrapper, payload);
      this.updateProgress(payload.completion_percentage);
      const confirmation = payload.required_count > 1 ? `${payload.completion_count}/${payload.required_count} confirmed` : "Saved";
      this.showSaveStatus(status, payload.workflow?.moved ? payload.workflow.message : confirmation, "text-emerald-500");
      this.applyWorkflow(payload.workflow);
      window.setTimeout(() => status?.classList.add("hidden"), 1400);
    } catch (error) {
      checkbox.checked = !checkbox.checked;
      this.showSaveStatus(status, error.message, "text-red-500");
    } finally {
      checkbox.disabled = false;
    }
  }

  updateConfirmationState(wrapper, payload) {
    if (!wrapper) return;

    const completedIds = new Set(payload.completed_user_ids || []);
    wrapper.querySelectorAll("[data-checklist-assignee-id]").forEach((chip) => {
      const confirmed = completedIds.has(chip.dataset.checklistAssigneeId);
      chip.classList.toggle("text-emerald-600", confirmed);
      chip.classList.toggle("dark:text-emerald-400", confirmed);
      chip.classList.toggle("text-slate-400", !confirmed);
    });

    const confirmationCount = wrapper.querySelector("[data-checklist-confirmation-count]");
    if (confirmationCount) confirmationCount.textContent = `${payload.completion_count}/${payload.required_count} confirmed`;

    const title = wrapper.querySelector("[data-checklist-item-title]");
    title?.classList.toggle("line-through", payload.completed);
    title?.classList.toggle("opacity-60", payload.completed);
  }

  applyWorkflow(workflow) {
    if (!workflow?.moved || !this.hasTaskIdValue) return;

    const card = document.getElementById(`task_${this.taskIdValue}`);
    const target = document.getElementById(`list_${workflow.target_list_id}_tasks`);
    if (!card || !target) return;

    const source = card.closest("[data-sortable-group-value='tasks']");
    card.dataset.taskStatus = workflow.task_status;
    target.append(card);
    this.refreshCelebrationCount(source);
    this.refreshCelebrationCount(target);
  }

  refreshCelebrationCount(container) {
    const list = container?.closest("[data-controller~='list-celebration']");
    if (!list) return;
    const count = container.querySelectorAll(".task-card").length;
    list.dataset.listCelebrationTasksCountValue = count;
    const countLabel = list.querySelector("[data-list-task-count]");
    if (countLabel) countLabel.textContent = count;
  }

  updateProgress(percentage) {
    if (this.hasProgressTarget) this.progressTarget.setAttribute("aria-valuenow", percentage);
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percentage}%`;
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = `${percentage}%`;
  }

  showSaveStatus(element, message, colorClass) {
    if (!element) return;

    element.textContent = message;
    element.classList.remove("hidden", "text-slate-400", "text-emerald-500", "text-red-500");
    element.classList.add(colorClass);
  }

  // Count current items
  updateCount() {
    const items = this.itemsTarget.querySelectorAll('.checklist-item');
    this.currentCountValue = items.length;
  }

  // Check if we can add more
  canAdd(count = 1) {
    this.updateCount();
    if ((this.currentCountValue + count) > this.maxItemsValue) {
      this.showWarning(`Maximum ${this.maxItemsValue} subtasks allowed. You have ${this.currentCountValue}.`);
      return false;
    }
    this.hideWarning();
    return true;
  }

  showWarning(message) {
    if (this.hasLimitWarningTarget) {
      this.limitWarningTarget.textContent = message;
      this.limitWarningTarget.classList.remove('hidden');
    } else {
      alert(message);
    }
  }

  hideWarning() {
    if (this.hasLimitWarningTarget) {
      this.limitWarningTarget.classList.add('hidden');
    }
  }

  // Add the title from the single-item field.
  add(e) {
    e.preventDefault();
    if (!this.hasSingleInputTarget) return;

    const title = this.singleInputTarget.value.trim();
    if (!title) {
      this.singleInputTarget.focus();
      return;
    }
    if (!this.canAdd(1)) return;

    this.insertItem(title);
    this.singleInputTarget.value = "";
    this.updateCount();
  }

  // Toggle bulk add textarea
  toggleBulkAdd() {
    if (this.hasBulkAddContainerTarget) {
      this.bulkAddContainerTarget.classList.toggle("hidden");
      if (!this.bulkAddContainerTarget.classList.contains("hidden") && this.hasBulkInputTarget) {
        this.bulkInputTarget.focus();
      }
    }
  }

  // Add multiple items from pasted list
  addBulk(e) {
    e.preventDefault();
    if (!this.hasBulkInputTarget) return;
    
    const text = this.bulkInputTarget.value.trim();
    if (!text) return;

    const lines = text.split("\n").filter(line => line.trim() !== "");
    
    if (lines.length === 0) return;

    // Limit bulk add to maxBulkItems
    if (lines.length > this.maxBulkItemsValue) {
      this.showWarning(`Maximum ${this.maxBulkItemsValue} items per bulk add. You pasted ${lines.length}.`);
      return;
    }

    // Check total limit
    if (!this.canAdd(lines.length)) return;

    // Add each line
    lines.forEach(line => this.insertItem(line.trim()));
    
    this.bulkInputTarget.value = "";
    this.toggleBulkAdd();
    this.updateCount();
  }

  // Add single on Enter
  addSingle(event) {
    if (event.key && event.key !== "Enter") return;
    event.preventDefault();
    if (!this.hasSingleInputTarget) return;
    
    const title = this.singleInputTarget.value.trim();
    if (!title) return;

    if (!this.canAdd(1)) return;

    this.insertItem(title);
    this.singleInputTarget.value = "";
    this.updateCount();
  }

  insertItem(title = "") {
    const timestamp = new Date().getTime() + Math.random();
    const template = document.createElement("template");
    template.innerHTML = this.templateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      timestamp,
    );
    const titleInput = template.content.querySelector('input[name$="[title]"]');
    const positionInput = template.content.querySelector('input[name$="[position]"]');
    if (titleInput) titleInput.value = title;
    if (positionInput) {
      positionInput.value =
        this.itemsTarget.querySelectorAll(".checklist-item").length + 1;
    }
    this.itemsTarget.append(template.content);
  }

  // Remove item
  remove(e) {
    e.preventDefault();
    const wrapper = e.target.closest('.checklist-item');
    if (wrapper.dataset.newRecord === "true") {
      wrapper.remove();
    } else {
      wrapper.style.display = 'none';
      const destroyInput = wrapper.querySelector("input[name*='_destroy']");
      if (destroyInput) destroyInput.value = "1";
    }
    this.updateCount();
  }

}
